import { useMemo, useState, type MouseEvent } from 'react';
import { EyeOff, Mail, Smartphone } from 'lucide-react';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { Skeleton } from 'components/ui/skeleton';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { useTranslation } from 'react-i18next';
import { User } from '/types/user-type';
import { MfaDialogState } from '../../enums/mfa-dialog-state.enum';
import { UserMfaType } from '../../enums/user-mfa-type-enum';
import { isMfaMethodAvailable } from '../../types/mfa.types';
import { useDisableUserMfa, useMfaTemplate } from '../../hooks/use-mfa';

type MfaMethodsCardProps = {
  userInfo: User | undefined;
  isLoading?: boolean;
  onSelectMethod: (dialogState: MfaDialogState) => void;
};

export const MfaMethodsCard = ({
  userInfo,
  isLoading = false,
  onSelectMethod,
}: MfaMethodsCardProps) => {
  const { t } = useTranslation();
  const { data: mfaConfig } = useMfaTemplate();
  const { mutateAsync: disableUserMfa, isPending: isDisablingMfa } = useDisableUserMfa();
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState(false);

  const isAuthenticatorAppEnabled = useMemo(
    () => isMfaMethodAvailable(mfaConfig, UserMfaType.AUTHENTICATOR_APP),
    [mfaConfig]
  );

  const isEmailVerificationEnabled = useMemo(
    () => isMfaMethodAvailable(mfaConfig, UserMfaType.EMAIL_VERIFICATION),
    [mfaConfig]
  );

  const isMfaEnabled = Boolean(userInfo?.mfaEnabled);
  const isEmailActive =
    isMfaEnabled &&
    Boolean(userInfo?.isMfaVerified) &&
    Number(userInfo?.userMfaType) === UserMfaType.EMAIL_VERIFICATION;
  const isAuthenticatorActive =
    isMfaEnabled &&
    Boolean(userInfo?.isMfaVerified) &&
    Number(userInfo?.userMfaType) === UserMfaType.AUTHENTICATOR_APP;

  const ActiveBadge = () => (
    <span className="inline-flex shrink-0 items-center rounded-full border border-success bg-success-background px-2 py-0.5 text-xs font-medium leading-none text-success">
      {t('ACTIVE', 'Active')}
    </span>
  );

  const handleEmailClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isEmailActive) {
      setIsDisableConfirmOpen(true);
      return;
    }
    if (!isEmailVerificationEnabled) return;
    onSelectMethod(MfaDialogState.EMAIL_VERIFICATION);
  };

  const handleAuthenticatorClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isAuthenticatorActive) {
      setIsDisableConfirmOpen(true);
      return;
    }
    if (!isAuthenticatorAppEnabled) return;
    onSelectMethod(MfaDialogState.AUTHENTICATOR_APP_SETUP);
  };

  const handleNoneClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDisableConfirmOpen(true);
  };

  const handleConfirmDisable = async () => {
    if (!userInfo?.itemId) return;
    await disableUserMfa(userInfo.itemId);
  };

  return (
    <Card className="w-full border border-border rounded-lg shadow-sm bg-card">
      <CardContent className="flex flex-col gap-4 p-4 sm:p-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl text-high-emphasis font-semibold">
            {t('MULTI_FACTOR_AUTHENTICATION', 'Multi-factor Authentication')}
          </h1>
          <p className="text-sm text-medium-emphasis">
            {t(
              'MFA_ENABLE_DESCRIPTION',
              'Enabling Multi-Factor Authentication (MFA) is a simple yet powerful way to protect your online presence. By adding an extra layer of security, MFA significantly reduces the risk of unauthorized access, even if your password is compromised. Take this important step to safeguard your digital identity.'
            )}
          </p>
        </div>

        <div className="border border-border rounded-md overflow-hidden">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={`mfa-skeleton-${index}`}>
                {index > 0 && <Separator />}
                <div className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-md" />
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-56" />
                    </div>
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            ))
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-surface rounded-md shrink-0">
                    <Mail className="text-secondary" size={24} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-high-emphasis">{t('EMAIL')}</h3>
                      {isEmailActive && <ActiveBadge />}
                    </div>
                    <p className="text-xs text-medium-emphasis">
                      {t(
                        'MFA_EMAIL_DESCRIPTION',
                        'Receive a verification code in your registered email to complete sign-in.'
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-md min-w-[88px] shrink-0"
                  disabled={!isEmailActive && !isEmailVerificationEnabled}
                  onClick={handleEmailClick}
                >
                  {isEmailActive ? t('DISABLE') : t('ENABLE')}
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between gap-4 p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-surface rounded-md shrink-0">
                    <Smartphone className="text-secondary" size={24} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-high-emphasis">
                        {t('AUTHENTICATOR_APP')}
                      </h3>
                      {isAuthenticatorActive && <ActiveBadge />}
                    </div>
                    <p className="text-xs text-medium-emphasis">
                      {t(
                        'MFA_AUTHENTICATOR_DESCRIPTION',
                        'Use an authentication app or browser extension to get two-factor authentication codes when prompted.'
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-md min-w-[88px] shrink-0"
                  disabled={!isAuthenticatorActive && !isAuthenticatorAppEnabled}
                  onClick={handleAuthenticatorClick}
                >
                  {isAuthenticatorActive ? t('DISABLE') : t('ENABLE')}
                </Button>
              </div>
              <Separator />
              {/* <div className="flex items-center justify-between gap-4 p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-surface rounded-md shrink-0">
                    <EyeOff className="text-secondary" size={24} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-high-emphasis">
                        {t('MFA_NONE', 'None')}
                      </h3>
                      {isMfaEnabled && <ActiveBadge />}
                    </div>
                    <p className="text-xs text-medium-emphasis">
                      {t('MFA_NONE_DESCRIPTION', 'No two-factor authentication.')}
                    </p>
                  </div>
                </div>
                {isMfaEnabled && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-md min-w-[88px] shrink-0"
                    onClick={handleNoneClick}
                  >
                    {t('DISABLE')}
                  </Button>
                )}
              </div> */}
            </>
          )}
        </div>
      </CardContent>
      <ConfirmationModal
        nested
        bgBlurred
        open={isDisableConfirmOpen}
        onOpenChange={setIsDisableConfirmOpen}
        title={t('DISABLE_MFA', 'Disable MFA?')}
        description={t(
          'DISABLE_MFA_DESCRIPTION',
          "Are you sure you want to disable MFA? You'll be asked to verify yourself."
        )}
        confirmText={t('CONFIRM', 'Confirm')}
        cancelText={t('CANCEL', 'Cancel')}
        isLoading={isDisablingMfa}
        onConfirm={handleConfirmDisable}
      />
    </Card>
  );
};
